import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Eye, EyeOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/** v6.2 unified /set-password page — mode='welcome' or 'reset' from server. */
export default function SetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/v62/set-password/validate?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setInfo(data));
  }, [token]);

  const rules = [
    { ok: pw.length >= 8, label: "At least 8 characters" },
    { ok: /[A-Z]/.test(pw), label: "One uppercase letter" },
    { ok: /[a-z]/.test(pw), label: "One lowercase letter" },
    { ok: /\d/.test(pw), label: "One number" },
  ];
  const allOk = rules.every((r) => r.ok);
  const match = pw && confirm && pw === confirm;

  const submit = async () => {
    if (!allOk || !match) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/v62/set-password`, { token, password: pw });
      setDone(data.mode);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!token || (info && !info.valid)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="cw-card p-8 w-full max-w-md" data-testid="set-password-expired">
          <h1 className="cw-h2">This link has expired</h1>
          <p className="text-sm text-[var(--cw-muted)] mt-2">The link is no longer valid.</p>
          <button onClick={() => nav("/forgot-password")}
            className="cw-btn-primary text-sm mt-5">Send a new link</button>
        </div>
      </div>
    );
  }
  if (!info) return <div className="p-8 text-sm text-[var(--cw-muted)]">Loading…</div>;

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="cw-card p-8 w-full max-w-md text-center" data-testid="set-password-done">
          <CheckCircle2 size={36} className="mx-auto text-[#2C9A52]" />
          <h1 className="cw-h2 mt-3">
            {done === "welcome" ? "You're all set" : "Password updated"}
          </h1>
          <button onClick={() => nav("/login")} className="cw-btn-primary text-sm mt-5">Go to sign in</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--cw-bg)" }}>
      <div className="cw-card p-8 w-full max-w-md" data-testid="set-password-page">
        <h1 className="cw-h2">
          {info.mode === "welcome" ? "Welcome to CompeteWiser" : "Set a new password"}
        </h1>
        <div className="text-xs text-[var(--cw-muted)] mt-1">{info.email}</div>
        <div className="mt-4 space-y-3">
          <div className="relative">
            <input type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)}
              placeholder={info.mode === "welcome" ? "Password" : "New password"}
              className="w-full h-10 px-3 pr-10 rounded-md border text-sm"
              style={{ borderColor: "var(--cw-border)" }} data-testid="sp-password" />
            <button className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--cw-muted)]"
              onClick={() => setShow(!show)} type="button">
              {show ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          <input type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm password"
            className="w-full h-10 px-3 rounded-md border text-sm"
            style={{ borderColor: "var(--cw-border)" }} data-testid="sp-confirm" />
          {confirm && pw !== confirm && (
            <div className="text-xs text-[#B02B2B]">Passwords don't match.</div>
          )}
          <ul className="text-xs text-[var(--cw-muted)] space-y-0.5">
            {rules.map((r, i) => (
              <li key={i} className={r.ok ? "text-[#1F6E3A]" : ""}>
                {r.ok ? "✓" : "○"} {r.label}
              </li>
            ))}
          </ul>
        </div>
        <button onClick={submit} disabled={busy || !allOk || !match}
          className="cw-btn-primary text-sm mt-5 w-full disabled:opacity-50"
          data-testid="sp-submit">
          {busy ? "Saving…" : (info.mode === "welcome" ? "Set password" : "Update password")}
        </button>
      </div>
    </div>
  );
}
